import React, { useState, useRef, useEffect } from 'react';
import { fetchOpenAIText } from '../../utils/openaiClient';
import './WordDefinitionExercise.css';

function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const WordDefinitionExercise = ({ pairs: initialPairs, title, level, onClose, updateAttributes, node, editor, getPos, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [mode, setMode] = useState('matching'); // 'matching' | 'glossary'
  // eslint-disable-next-line no-unused-vars
  const [matches, setMatches] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [showResult, setShowResult] = useState(false);
  const [shuffledDefs, setShuffledDefs] = useState(() => shuffle(initialPairs.map(p => p.definition)));
  const [pairs, setPairs] = useState(initialPairs);
  const [editing, setEditing] = useState({ idx: null, field: null });
  const [editValue, setEditValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editablePairs, setEditablePairs] = useState([...initialPairs]);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const addBtnRef = useRef(null);
  const containerRef = useRef(null);
  const rowRefs = useRef({});
  const genTimersRef = useRef({});
  const lastGeneratedRef = useRef({});
  const [isGenerating, setIsGenerating] = useState({});

  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);
  const shuffleInitializedRef = useRef(false);

  const autoResizeRow = (index) => {
    const refs = rowRefs.current[index];
    if (!refs) return;
    const { word, def } = refs;
    // These fields are box-sizing: border-box, but scrollHeight only ever
    // reports content + padding (never border) — setting style.height
    // straight from scrollHeight under-sizes the box by the border width,
    // clipping the top/bottom of the text. Add the border back in.
    const borderY = (el) => {
      const cs = window.getComputedStyle(el);
      return (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    };
    if (word) {
      word.style.height = 'auto';
      word.style.height = `${word.scrollHeight + borderY(word)}px`;
    }
    if (def) {
      def.style.height = 'auto';
      def.style.height = `${def.scrollHeight + borderY(def)}px`;
    }
    if (word && def) {
      const maxH = Math.max(word.scrollHeight + borderY(word), def.scrollHeight + borderY(def));
      word.style.height = `${maxH}px`;
      def.style.height = `${maxH}px`;
    }
  };

  const registerRef = (index, key) => (el) => {
    if (!rowRefs.current[index]) rowRefs.current[index] = {};
    rowRefs.current[index][key] = el;
    if (el) {
      requestAnimationFrame(() => autoResizeRow(index));
    }
  };

  useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => {
        pairs.forEach((_, i) => autoResizeRow(i));
      });
    }
  }, [isEditing, pairs]);

  const maybeGenerateDefinition = async (index, wordValue) => {
    const word = String(wordValue || '').trim();
    if (!word) return;
    const current = pairs[index];
    if (!current || (current.definition && current.definition.trim().length > 0)) return;
    if (lastGeneratedRef.current[index] === word) return;
    const levelSpecs = {
      A1: { maxWords: 10, temp: 0.1, style: 'Use A1 words only. One simple clause. No idioms. Avoid abstract terms.', example: 'big: large in size; quiet: making little sound' },
      A2: { maxWords: 14, temp: 0.1, style: 'Use A2 everyday words. One simple sentence. No relative clauses or advanced words.', example: 'borrow: take and return later; crowded: full of people' },
      B1: { maxWords: 18, temp: 0.15, style: 'Use B1 words. Clear neutral style. Avoid academic/rare terms.', example: 'flexible: can change or bend easily; efficient: doing work well without waste' },
      B2: { maxWords: 20, temp: 0.2, style: 'Use B2 words. You may use one relative clause. Keep concise.', example: 'sustainable: can continue without causing harm; justify: give good reasons for' },
      C1: { maxWords: 22, temp: 0.2, style: 'Advanced but concise academic tone (C1). Prefer precise, high-utility words.', example: 'ambiguous: having more than one possible meaning; facilitate: make a process easier' },
      C2: { maxWords: 22, temp: 0.2, style: 'Very advanced, precise and concise (C2). One sentence only.', example: 'ubiquitous: present everywhere; empirical: based on observed evidence' },
    };
    const spec = levelSpecs[editableLevel] || { maxWords: 18, temp: 0.15, style: 'Use intermediate words. Clear structure.', example: '' };

    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const targetRe = new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i');

    const buildPrompt = (strict = false) => `You are a CEFR-aware lexicographer. Write ONE short English definition that matches the target level.\n\nTarget word/phrase: "${word}"\nLevel: ${editableLevel}\nGuidelines: ${spec.style}\nStyle examples for this level (do not copy, only mimic complexity): ${spec.example}\nHard constraints:\n- Do NOT use the target word (or any inflected form) in the definition\n- Exactly one sentence\n- Max ${spec.maxWords} words\n- Plain text only (no quotes, no markdown, no examples)\n- No meta phrases like "a word that means" or "term for"\n${strict ? '- STRICT retry: If uncertain, prefer a generic hypernym that fits the level.' : ''}`;

    const countWords = (s) => (String(s || '').trim().split(/\s+/).filter(Boolean).length);
    const tooComplexFor = (s, lvl) => {
      const txt = String(s || '');
      if (lvl === 'A1' || lvl === 'A2') {
        if (/[,:;]/.test(txt)) return true;
        if (/(which|that|because|although|despite|furthermore|however|therefore)\b/i.test(txt)) return true;
      }
      return false;
    };

    try {
      setIsGenerating(prev => ({ ...prev, [index]: true }));
      let text = await fetchOpenAIText({ prompt: buildPrompt(false), temperature: spec.temp, maxTokens: 120 });
      let firstLine = String(text || '').split('\n')[0].trim();
      if (targetRe.test(firstLine)) {
        text = await fetchOpenAIText({ prompt: buildPrompt(true), temperature: Math.max(0.05, spec.temp - 0.05), maxTokens: 120 });
        firstLine = String(text || '').split('\n')[0].trim();
        if (targetRe.test(firstLine)) {
          firstLine = firstLine.replace(targetRe, 'the term');
        }
      }
      if (countWords(firstLine) > spec.maxWords || tooComplexFor(firstLine, editableLevel)) {
        const refinePrompt = `Rewrite this definition to strictly match CEFR ${editableLevel} constraints. Keep meaning, but simplify or shorten. One sentence, max ${spec.maxWords} words, ${spec.style}. Do not use the target word.\nDefinition: ${firstLine}`;
        const refined = await fetchOpenAIText({ prompt: refinePrompt, temperature: Math.max(0.05, spec.temp - 0.05), maxTokens: 120 });
        const refinedLine = String(refined || '').split('\n')[0].trim();
        if (refinedLine) firstLine = refinedLine;
      }

      setPairs(prev => prev.map((p, i) => i === index ? { ...p, definition: firstLine } : p));
      requestAnimationFrame(() => autoResizeRow(index));
      lastGeneratedRef.current[index] = word;
    } catch (_) {
      // silent
    } finally {
      setIsGenerating(prev => ({ ...prev, [index]: false }));
    }
  };

  useEffect(() => {
    return () => {
      const timers = genTimersRef.current || {};
      Object.values(timers).forEach(id => clearTimeout(id));
      genTimersRef.current = {};
    };
  }, []);

  const [selectedWord, setSelectedWord] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]); // [{word, definition}]
  const [errorPair, setErrorPair] = useState(null); // {word, definition}
  const [flashPair, setFlashPair] = useState(null); // highlight correct pair before hiding
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [firstTrySuccess, setFirstTrySuccess] = useState(0);
  const [resultsVisible, setResultsVisible] = useState(false);
  const attemptsRef = useRef({});

  // --- Shuffle init: broadcast own order so both sides sync ---
  useEffect(() => {
    if (pairs.length === 0 || shuffleInitializedRef.current) return;
    shuffleInitializedRef.current = true;
    const shuffled = shuffle(pairs.map(p => p.definition));
    setShuffledDefs(shuffled);
    updateState({ shuffledDefs: shuffled });
  }, [pairs]); // eslint-disable-line

  // --- Restore state on mount (own or other side's) ---
  useEffect(() => {
    if (syncedRef.current) return;
    const ownPairs = Array.isArray(myState.matchedPairs) && myState.matchedPairs.length > 0 ? myState.matchedPairs : null;
    const otherPairs = Array.isArray(otherState.matchedPairs) && otherState.matchedPairs.length > 0 ? otherState.matchedPairs : null;
    const pairsToRestore = ownPairs || otherPairs;
    const resultsToRestore = myState.resultsVisible || otherState.resultsVisible;
    if (!pairsToRestore && !resultsToRestore) return;
    syncedRef.current = true;
    if (pairsToRestore) setMatchedPairs(pairsToRestore);
    if (resultsToRestore || (pairsToRestore && pairsToRestore.length === initialPairs.length)) setResultsVisible(true);
    const wa = typeof myState.wrongAttempts === 'number' ? myState.wrongAttempts : otherState.wrongAttempts;
    const fts = typeof myState.firstTrySuccess === 'number' ? myState.firstTrySuccess : otherState.firstTrySuccess;
    if (typeof wa === 'number') setWrongAttempts(wa);
    if (typeof fts === 'number') setFirstTrySuccess(fts);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Real-time: adopt other side's shuffle order ---
  useEffect(() => {
    if (!Array.isArray(otherState.shuffledDefs) || otherState.shuffledDefs.length === 0) return;
    setShuffledDefs(otherState.shuffledDefs);
  }, [otherState.shuffledDefs]); // eslint-disable-line

  // --- Real-time: mirror other side's matched pairs ---
  useEffect(() => {
    if (!Array.isArray(otherState.matchedPairs)) return;
    setMatchedPairs(otherState.matchedPairs);
  }, [otherState.matchedPairs]); // eslint-disable-line

  // --- Real-time: show results when other side finishes ---
  useEffect(() => {
    if (!otherState.resultsVisible) return;
    setResultsVisible(true);
    if (Array.isArray(otherState.matchedPairs) && otherState.matchedPairs.length > 0) {
      setMatchedPairs(otherState.matchedPairs);
    }
    if (typeof otherState.wrongAttempts === 'number') setWrongAttempts(otherState.wrongAttempts);
    if (typeof otherState.firstTrySuccess === 'number') setFirstTrySuccess(otherState.firstTrySuccess);
  }, [otherState.resultsVisible]); // eslint-disable-line

  // --- Real-time: Try Again from other side ---
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setResultsVisible(false);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    attemptsRef.current = {};
    syncedRef.current = false;
    if (Array.isArray(otherState.shuffledDefs)) setShuffledDefs(otherState.shuffledDefs);
  }, [otherState.resetAt]); // eslint-disable-line

  const getWordClass = (word) => {
    if (matchedPairs.some(p => p.word === word)) return 'wd-item matched';
    const classes = ['wd-item'];
    if (flashPair?.word === word || otherState.flashPair?.word === word) classes.push('correct');
    if (errorPair?.word === word || otherState.errorPair?.word === word) classes.push('incorrect');
    if (selectedWord === word) classes.push('selected');
    else if (otherState.selectedWord === word && selectedWord === null) classes.push('selected');
    return classes.join(' ');
  };

  const getDefClass = (definition) => {
    if (matchedPairs.some(p => p.definition === definition)) return 'wd-item matched';
    const classes = ['wd-item'];
    if (flashPair?.definition === definition || otherState.flashPair?.definition === definition) classes.push('correct');
    if (errorPair?.definition === definition || otherState.errorPair?.definition === definition) classes.push('incorrect');
    if (selectedWord) classes.push('selectable');
    return classes.join(' ');
  };

  const handleWordClick = (word) => {
    if (matchedPairs.some(p => p.word === word)) return;
    setSelectedWord(word);
    updateState({ selectedWord: word });
  };

  const handleDefinitionClick = (definition) => {
    if (!selectedWord) return;
    if (matchedPairs.some(p => p.definition === definition)) return;

    const correct = pairs.find(p => p.word === selectedWord)?.definition === definition;
    if (correct) {
      const word = selectedWord;
      const capturedFirstTrySuccess = firstTrySuccess;
      const capturedWrongAttempts = wrongAttempts;
      const isFirstTry = (attemptsRef.current[word] || 0) === 0;

      const fp = { word, definition };
      setFlashPair(fp);
      setSelectedWord(null);
      updateState({ flashPair: fp, selectedWord: null });

      setTimeout(() => {
        const newFirstTrySuccess = capturedFirstTrySuccess + (isFirstTry ? 1 : 0);
        setMatchedPairs(prev => {
          const newMatchedPairs = [...prev, { word, definition }];
          const isAllMatched = newMatchedPairs.length === pairs.length;
          if (isAllMatched) {
            setResultsVisible(true);
            updateState({ matchedPairs: newMatchedPairs, resultsVisible: true, wrongAttempts: capturedWrongAttempts, firstTrySuccess: newFirstTrySuccess, flashPair: null });
          } else {
            updateState({ matchedPairs: newMatchedPairs, flashPair: null });
          }
          return newMatchedPairs;
        });
        if (isFirstTry) setFirstTrySuccess(newFirstTrySuccess);
        setFlashPair(null);
        attemptsRef.current[word] = (attemptsRef.current[word] || 0) + 1;
      }, 600);
    } else {
      const ep = { word: selectedWord, definition };
      setErrorPair(ep);
      attemptsRef.current[selectedWord] = (attemptsRef.current[selectedWord] || 0) + 1;
      setWrongAttempts(prev => prev + 1);
      setSelectedWord(null);
      updateState({ errorPair: ep, selectedWord: null });
      setTimeout(() => {
        setErrorPair(null);
        updateState({ errorPair: null });
      }, 700);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCheck = () => {
    setShowResult(true);
  };

  const handleTryAgain = () => {
    const newShuffle = shuffle(pairs.map(p => p.definition));
    setShowResult(false);
    setMatches({});
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    attemptsRef.current = {};
    setResultsVisible(false);
    setShuffledDefs(newShuffle);
    syncedRef.current = false;
    updateState({
      matchedPairs: [], resultsVisible: false, wrongAttempts: 0, firstTrySuccess: 0,
      selectedWord: null, flashPair: null, errorPair: null,
      shuffledDefs: newShuffle, resetAt: Date.now()
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleUnmix = () => {
    setMode(mode === 'matching' ? 'glossary' : 'matching');
    setShowResult(false);
    setMatches({});
    setShuffledDefs(shuffle(pairs.map(p => p.definition)));
  };

  // eslint-disable-next-line no-unused-vars
  const startEdit = (idx, field, value) => {
    setEditing({ idx, field });
    setEditValue(value);
  };

  const commitEdit = () => {
    if (editing.idx === null) return;
    setPairs(prev => prev.map((p, i) =>
      i === editing.idx ? { ...p, [editing.field]: editValue } : p
    ));
    setEditing({ idx: null, field: null });
    setEditValue('');
  };

  // eslint-disable-next-line no-unused-vars
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditing({ idx: null, field: null });
      setEditValue('');
    }
  };

  const handleAddPair = () => {
    const newPair = { word: '', definition: '', id: `pair_${Date.now()}` };
    setPairs(prev => [...prev, newPair]);
  };

  const handleDeletePair = (index) => {
    if (pairs.length <= 1) return;
    setPairs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        pairs: pairs,
        title: editableTitle,
        level: editableLevel,
      });
    }
    // Pairs may have changed (words/definitions added, removed, or edited)
    // — shuffledDefs only gets built once (on mount), so without this it
    // stays stale and newly-added definitions never appear in the
    // play-mode Definitions column. Reshuffle from the current pairs and
    // reset progress, same as "Try Again", since the underlying data just
    // changed.
    const newShuffle = shuffle(pairs.map(p => p.definition));
    setShuffledDefs(newShuffle);
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorPair(null);
    setFlashPair(null);
    setWrongAttempts(0);
    setFirstTrySuccess(0);
    attemptsRef.current = {};
    setResultsVisible(false);
    syncedRef.current = false;
    updateState({
      matchedPairs: [], resultsVisible: false, wrongAttempts: 0, firstTrySuccess: 0,
      selectedWord: null, flashPair: null, errorPair: null,
      shuffledDefs: newShuffle, resetAt: Date.now()
    });
  };

  // Results panel data — same ring/tier pattern as ABCD/Multiple
  // Choice/Word Translation, adapted to this exercise's own metrics: every
  // word eventually gets matched, so "score" here is first-try accuracy.
  let resultsData = null;
  if (resultsVisible) {
    const total = pairs.length || 1;
    const ratio = firstTrySuccess / total;
    const percent = Math.round(ratio * 100);
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'poor';
    let headline;
    let subline;
    if (ratio >= 0.9) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (ratio >= 0.7) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (ratio >= 0.5) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'poor'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, circumference, dashOffset, tier, headline, subline };
  }

  return (
    <div className="wde-card">
      <div className="wd-header">
        <div className="wd-header-left">
          <div className="wd-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z"/>
              <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-3"/>
              <path d="M8 7h8M8 11h5"/>
            </svg>
          </div>
          {isEditing ? (
            <input type="text" value={editableTitle} onChange={e => setEditableTitle(e.target.value)} className="wd-title-input" />
          ) : (
            <span className="wd-title">{editableTitle}</span>
          )}
          <span className="wd-level-badge">{editableLevel}</span>
        </div>
        <div className="wd-header-actions">
          {!isEditing && !isStudent && (
            <button className="wd-icon-btn" onClick={() => setIsEditing(true)} aria-label="Edit" data-tip="Edit">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="wd-icon-btn is-save" onClick={handleSave} aria-label="Save" data-tip="Save">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="wd-icon-btn is-cancel" onClick={() => { setIsEditing(false); setEditableTitle(title); setEditableLevel(level); }} aria-label="Cancel" data-tip="Cancel">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="wd-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="wd-body">
        {(mode === 'matching' && !isEditing) ? (
          <div className="wd-matching-section" ref={containerRef}>
            {!resultsVisible ? (
              <>
                <div className="wd-matching-container">
                  <div className="wd-column">
                    <div className="wd-column-title">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z"/>
                        <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-3"/>
                      </svg>
                      Words
                    </div>
                    {pairs.map(({ word }, i) => (
                      matchedPairs.some(p => p.word === word) ? null : (
                        <div key={i} className={getWordClass(word)} data-word={word} onClick={() => handleWordClick(word)}>
                          {word}
                        </div>
                      )
                    ))}
                  </div>

                  <div className="wd-column">
                    <div className="wd-column-title">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <circle cx="11" cy="11" r="7"/>
                        <path d="m20 20-3.5-3.5"/>
                      </svg>
                      Definitions
                    </div>
                    {shuffledDefs.map((definition, j) => (
                      matchedPairs.some(p => p.definition === definition) ? null : (
                        <div key={j} className={getDefClass(definition)} data-definition={definition} onClick={() => handleDefinitionClick(definition)}>
                          {definition}
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {matchedPairs.length > 0 && (
                  <div className="wd-matched-list">
                    {matchedPairs.map((p, idx) => (
                      <div key={idx} className="wd-matched-row">
                        <span className="wd-matched-word">{p.word}</span>
                        <span className="wd-matched-sep">→</span>
                        <span className="wd-matched-def">{p.definition}</span>
                      </div>
                    ))}
                  </div>
                )}

                {matchedPairs.length > 0 && (
                  <div className="wd-reset-row">
                    <button className="wd-reset-btn" onClick={handleTryAgain}>Start over</button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={`wd-results tier-${resultsData.tier}`}>
                  <div className="wd-results-ring-wrap">
                    <svg viewBox="0 0 100 100" className="wd-results-ring" aria-hidden>
                      <circle cx="50" cy="50" r="42" className="wd-results-ring-track" />
                      <circle
                        cx="50"
                        cy="50"
                        r="42"
                        className="wd-results-ring-progress"
                        style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                      />
                    </svg>
                    <div className="wd-results-ring-center">
                      <div className="wd-results-percent">{resultsData.percent}%</div>
                      <div className="wd-results-percent-label">first try</div>
                    </div>
                  </div>
                  <div className="wd-results-headline">{resultsData.headline}</div>
                  <div className="wd-results-subline">{resultsData.subline}</div>
                  <div className="wd-results-stats">
                    <div className="wd-results-stat is-correct">
                      <span className="wd-results-stat-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </span>
                      <span className="wd-results-stat-value">{firstTrySuccess}</span>
                      <span className="wd-results-stat-label">first-try</span>
                    </div>
                    <div className="wd-results-stats-divider" aria-hidden />
                    <div className="wd-results-stat is-incorrect">
                      <span className="wd-results-stat-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </span>
                      <span className="wd-results-stat-value">{wrongAttempts}</span>
                      <span className="wd-results-stat-label">mistakes</span>
                    </div>
                  </div>
                  <button className="wd-results-cta" onClick={handleTryAgain}>
                    <span>Try Again</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="23 4 23 10 17 10"/>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                    </svg>
                  </button>
                </div>

                {(() => {
                  const list = matchedPairs.length > 0 ? matchedPairs : (Array.isArray(otherState.matchedPairs) ? otherState.matchedPairs : []);
                  return list.length > 0 ? (
                    <div className="wd-matched-list">
                      {list.map((p, idx) => (
                        <div key={idx} className="wd-matched-row">
                          <span className="wd-matched-word">{p.word}</span>
                          <span className="wd-matched-sep">→</span>
                          <span className="wd-matched-def">{p.definition}</span>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </div>
        ) : (mode === 'glossary' || isEditing) ? (
          <div className="wd-glossary">
            <ul className="wd-glossary-list">
              {pairs.map(({ word, definition, id }, i) => (
                <li key={id || i} className="wd-glossary-row">
                  <div className="wd-glossary-row-fields">
                    {isEditing ? (
                      <>
                        <textarea
                          value={word}
                          placeholder="Word"
                          rows={1}
                          ref={registerRef(i, 'word')}
                          onChange={e => {
                            const value = e.target.value;
                            setPairs(prev => prev.map((p, idx) => idx === i ? { ...p, word: value } : p));
                            requestAnimationFrame(() => autoResizeRow(i));
                            if (genTimersRef.current[i]) clearTimeout(genTimersRef.current[i]);
                            genTimersRef.current[i] = setTimeout(() => {
                              maybeGenerateDefinition(i, value);
                            }, 700);
                          }}
                          className="wd-glossary-word-input"
                        />
                        <span className="wd-glossary-def-wrap">
                          <textarea
                            value={definition}
                            placeholder="Definition"
                            rows={1}
                            ref={registerRef(i, 'def')}
                            onChange={e => {
                              const value = e.target.value;
                              setPairs(prev => prev.map((p, idx) => idx === i ? { ...p, definition: value } : p));
                              requestAnimationFrame(() => autoResizeRow(i));
                            }}
                            className={`wd-glossary-def-input ${isGenerating[i] ? 'generating' : ''}`}
                          />
                          {isGenerating[i] && <span className="wd-gen-spinner" aria-label="Generating definition" />}
                        </span>
                      </>
                    ) : (
                      <>
                        <b className="wd-glossary-word-label">{word}</b>
                        <span className="wd-glossary-def-label">{definition}</span>
                      </>
                    )}
                  </div>
                  {isEditing && pairs.length > 1 && (
                    <button
                      className="wd-delete-btn"
                      onClick={() => handleDeletePair(i)}
                      title="Delete pair"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {isEditing && (
              <button ref={addBtnRef} onClick={handleAddPair} className="wd-add-btn" title="Add word-definition pair">
                + Add new pair
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WordDefinitionExercise;
